import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

const ERROR_MESSAGES = {
  email_already_in_use: 'An account with this email already exists. Try signing in instead.',
  invalid_email: 'That doesn\'t look like a valid email address.',
  password_too_short: 'Pick a password at least 10 characters long.',
  invalid_organization_name: 'Organization name must be 2 to 80 characters.',
  missing_required_fields: 'Fill in every field to continue.',
  rate_limited: 'You\'re creating accounts too fast — wait a minute and try again.',
};

export default function Signup() {
  const { signup, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState(null);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    try {
      await signup({ email, password, organization_name: organizationName });
      // Card-first trial: the 14 days start at checkout, so land new accounts
      // on the plan picker instead of an empty (and quota-gated) dashboard.
      // A plan chosen on the landing pricing cards (?plan=…&cadence=…) is
      // forwarded so Billing's auto-checkout sends them straight to Stripe.
      const plan = searchParams.get('plan');
      const cadence = searchParams.get('cadence');
      const planQs = plan ? `&plan=${encodeURIComponent(plan)}&cadence=${encodeURIComponent(cadence || 'monthly')}` : '';
      navigate(`/account/billing?welcome=1${planQs}`, { replace: true });
    } catch (err) {
      const code = err.body?.error || err.message;
      setError(ERROR_MESSAGES[code] || 'Sign-up failed. Try again.');
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <header className="login-header">
          <div className="brand-mark">
            <img src="/icons/markaii-m.png?v=2" alt="" className="brand-mark-icon" />
            <span>Markaii</span>
          </div>
          <h1>Create your account</h1>
          <p className="login-sub">14-day free trial. $0 today — card required, first charge when the trial ends.</p>
        </header>

        <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
          <label className="login-field">
            <span>Organization name</span>
            <input
              type="text"
              autoComplete="organization"
              required
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Your company"
              maxLength={80}
              disabled={loading}
              autoFocus
            />
          </label>

          <label className="login-field">
            <span>Work email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
            />
          </label>

          <label className="login-field">
            <span>Password (10+ chars)</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              disabled={loading}
            />
          </label>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating…' : 'Create account'}
          </button>

          <p className="login-footnote">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
